import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/HomePage.css";
import { useAuth } from "../auth/useAuth";
import VintageAssistant from "../components/VintageAssistant";
import { getIdentifier, clearIdentifier } from "../utils/auth";

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, logout } = useAuth();
  const [displayName, setDisplayName] = useState("");

  const [unreadCount, setUnreadCount] = useState(0);

  const [hasCollection, setHasCollection] = useState(false);
  const [loading, setLoading] = useState(true);

  // mobile drawer state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const fetchUser = async () => {
  if (authLoading) return;

  if (!user) {
    setLoading(false);
    navigate("/returning", { replace: true });
    return;
  }

  try {
    const response = await fetch(
      "https://squid-app-6s2hc.ondigitalocean.app/get-user",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: user.email }),
      }
    );

    const data = await response.json();

   if (response.ok) {
  setHasCollection(data.hasCollection === true || data.hasCollection === "true");
  setDisplayName(data.username || data.name || "");
}

const unreadRes = await fetch(
  "https://squid-app-6s2hc.ondigitalocean.app/get-unread-count",
  {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      email: user.email
    })
  }
);

const unreadData = await unreadRes.json();

if (unreadRes.ok) {
  setUnreadCount(
    unreadData.count || 0
  );
}

  } catch {
    // no-op
  } finally {
    setLoading(false);
  }
};

const handleSignOut = async () => {
  try {
    if (logout) await logout();
  } finally {
    clearIdentifier();
    navigate("/welcome", { replace: true });
  }
};


useEffect(() => {
  setLoading(true);     // ✅ show loading while refreshing
  fetchUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [authLoading, user, location.key]); // ✅ rerun when route changes

useEffect(() => {
  const onFocus = () => {
    setLoading(true);
    fetchUser();
  };

  window.addEventListener("focus", onFocus);
  return () => window.removeEventListener("focus", onFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [authLoading, user]);


  // close drawer on Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const go = (path) => () => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const handleUpload = go("/upload-collection");
  const handleEdit = go("/edit-collection-selector");
  const handleView = go("/view-collection-selector");
  const handleDiscover = go("/discover");
  const handleProfile = go("/profile");
  const handleContact = go("/contact");
  const handleEditClick = go("/edit-collection-selector");
  const handleViewClick = go("/view-collection-selector");
  const openLearnMore = go("/about");
  const handleMessages = go("/notifications");

  if (loading) {
    return (
      <main className="homepage-container" role="main">
        <div className="home-loading" aria-live="polite">
          loading…
        </div>
      </main>
    );
  }

  return (
   <div className={`nav-shell ${mobileNavOpen ? "nav-shell--open" : ""}`}>
      {/* Backdrop (only clickable on mobile + open) */}
    <div
  className={`menu-overlay ${mobileNavOpen ? "open" : ""}`}
  onClick={() => setMobileNavOpen(false)}
/>

<aside className={`slide-menu ${mobileNavOpen ? "open" : ""}`}>
  <button
    className="menu-close"
    onClick={() => setMobileNavOpen(false)}
  >
    ×
  </button>

  <nav className="menu-nav">

   <button onClick={handleMessages}>
  inbox
  {unreadCount > 0 && (
    <span className="menu-message-count">
      ({unreadCount})
    </span>
  )}
</button>
   
    <button onClick={handleProfile}>
  profile
</button>

    <div className="menu-divider" />

     <button onClick={handleDiscover}>
      browse collections
    </button>

    <button onClick={openLearnMore}>
      learn more
    </button>

    <button onClick={handleContact}>
      contact t.c.p.
    </button>

  </nav>
</aside>
{/*
      <VintageAssistant howToUrl="https://thecollectorsplatform.com/#/about?tab=how-to" corner="br" 
      onView={handleViewClick} 
      onEdit={handleEditClick} />
/ */}
       {!mobileNavOpen && (
<header className="home-top-bar" aria-label="page controls">

  <button
    type="button"
    className="nav-burger"
    onClick={() => setMobileNavOpen(true)}
  >
    <span className="burger-icon">☰</span>

    {unreadCount > 0 && (
      <span className="nav-burger-count">
        {unreadCount}
      </span>
    )}
  </button>

  <button
    className="sign-out-button"
    onClick={handleSignOut}
  >
    sign out
  </button>

</header>
)}

      {/* Main */}
      <main className="homepage-container" role="main">
        <div className="home-header-actions">
</div>
        <div className="home-header">
  {!hasCollection ? (
    <>
      <h1 className="home-title">
        let's begin, {displayName}
      </h1>
      <p className="home-subtitle">
        start by uploading a few photos
      </p>
    </>
  ) : (
    <>
      <h1 className="home-title">
        welcome back, {displayName}
      </h1>
      <p className="home-subtitle">
        keep building your collection
      </p>
    </>
  )}
</div>
        <div
          className={`home-actions ${
            hasCollection ? "has-collections" : "no-collections"
          }`}
        >
          {!hasCollection ? (
            <button
              className="continue-button is-primary"
              type="button"
              onClick={handleUpload}
            >
              create your first collection
            </button>
          ) : (
            <>
              <button
                className="continue-button is-primary"
                type="button"
                onClick={handleUpload}
              >
                create new collection
              </button>
              <button
                className="continue-button"
                type="button"
                onClick={handleEdit}
              >
                edit collection(s)
              </button>
              <button
                className="continue-button"
                type="button"
                onClick={handleView}
              >
                view collection(s)
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
